import { Controller } from "stimulus";
import { Draggable } from "@shopify/draggable";
import Rails from '@rails/ujs';

export default class extends Controller {
  static values = {
    url: String,
    draggableSelector: String,
    handleSelector: String,
    cardSelector: String,
    collapseAccordionsOnDrag: Boolean,
    questionNumberFormat: String,
    reorderErrorMessage: String
  };

  static targets = ['item'];

  static outlets = ['components--accordion'];

  connect() {
    this.draggable = new Draggable(this.element, {
      draggable: this.draggableSelectorValue,
      handle: this.handleSelectorValue
    });

    this.draggable.on('drag:start', this._onDragStart.bind(this));
    this.draggable.on('mirror:created', this._onMirrorCreated.bind(this));
    this.draggable.on("drag:move", this._onDragMove.bind(this));
    this.draggable.on("drag:over", this._onDragOver.bind(this));
    this.draggable.on("drag:out", this._onDragOut.bind(this));
    this.draggable.on("drag:stop", this._onDragStop.bind(this));

    this.over = undefined;
    this.element.setAttribute('data-drag-initialized', '');
  }

  disconnect() {
    this.draggable.destroy();
    this.element.removeAttribute('data-drag-initialized');
  }

  _onDragStart(event) {
    this.element.classList.add('is-dragging');

    this._draggedId = event.data.source.dataset.id;
    this._originalOrderIds = this._liveItems().map(item => item.dataset.id);

    // Snapshot by name (stable string key) before cloneNode creates the mirror.
    this._checkedStateSnapshot = new Map();
    event.data.source
      .querySelectorAll('input[type="radio"], input[type="checkbox"]')
      .forEach(input => {
        if (input.name) {
          this._checkedStateSnapshot.set(input.name, input.checked);
        }
      });

    // Deferred one frame on purpose. Draggable measures the source to work out
    // where the cursor sits inside the mirror, and that measurement resolves in a
    // microtask after drag:start. Collapsing here synchronously shrinks the source
    // and shifts it up the page first, so the mirror ends up floating far away
    // from the cursor.
    if (this.collapseAccordionsOnDragValue) {
      requestAnimationFrame(() => this._collapseAccordions(event.data.source));
    }
  }

  _collapseAccordions(source) {
    const sel = '[data-components--accordion-target="content"]:not(.collapsed)';

    // The mirror lives inside this.element as well, so its own accordion
    // collapses along with the cards left behind on the page.
    this.element.querySelectorAll(sel).forEach(content => {
      content.classList.add('collapsed');
      content.style.height = '0';
      content.style.overflow = 'hidden';
    });

    const placeholder = source.querySelector('.placeholder');
    if (placeholder && this.mirror) {
      placeholder.style.height = `${this.mirror.getBoundingClientRect().height}px`;
    }
  }

  _onMirrorCreated(event) {
    const { mirror, source } = event.data;

    this.mirror = mirror;

    const dimensions = source.getBoundingClientRect();
    mirror.style.width = `${dimensions.width}px`;
    mirror.classList.add('z-50');

    // When the accordions collapse the mirror shrinks with them, so its height is
    // left to the content instead of being pinned to the expanded card.
    if (!this.collapseAccordionsOnDragValue) {
      mirror.style.height = `${dimensions.height}px`;
    }

    // Style the floating mirror card
    const mirrorCard = mirror.querySelector(this.cardSelectorValue);
    if (mirrorCard) {
      mirrorCard.classList.remove('border-white');
      mirrorCard.classList.add('border-purple-600', 'border-dashed', 'border');
    }

    // Hide source card so only the ghost placeholder occupies that slot
    const sourceCard = source.querySelector(this.cardSelectorValue);
    if (sourceCard) sourceCard.classList.add('hidden');

    const placeholder = source.querySelector('.placeholder');
    if (placeholder) {
      // Re-measured in _collapseAccordions once the cards have collapsed.
      placeholder.style.height = `${dimensions.height}px`;
      placeholder.classList.remove('hidden');
    }

    // The mirror is cloneNode(true) so its radio inputs share the same `name` as the
    // source. Strip names to prevent browser radio-group enforcement while the mirror
    // is visible. State is restored after drop in _onDragStop's requestAnimationFrame.
    mirror.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(input => {
      input.removeAttribute('name');
    });
  }

  _onDragMove(event) {
    if (!this.over) return;
    if (event.data.source === this.over) return;

    const sensorData = event.data.sensorEvent.data;
    const { hasCrossedHalfwayY } = this._calculateOverlapPercentage(sensorData);

    // Nothing moved across the halfway line, so the drop side is unchanged and the
    // numbers on screen already describe where the card would land.
    if (this.after === hasCrossedHalfwayY && this.before === !hasCrossedHalfwayY) {
      return;
    }

    this._removeAllDividers();
    this.over.classList.add(hasCrossedHalfwayY ? 'divider-below' : 'divider-above');
    this.after = hasCrossedHalfwayY;
    this.before = !hasCrossedHalfwayY;

    this._renumberItems(this._prospectiveOrder());
  }

  _onDragOver(event) {
    const over = event.data.over;
    if (!over) return;

    const pageType = over.dataset.pageType;
    const startOrEnd = pageType === 'start' || pageType === 'end';
    if (startOrEnd) return;

    if (this.over) {
      this._removeAllDividers();
    }

    this.over = over;
    this.before = null;
    this.after = null;
  }

  _onDragOut(_event) {
    this.over = null;
    this.before = null;
    this.after = null;
    this._removeAllDividers();

    // Back outside any card, so the drop would be a no-op: show the current order.
    this._renumberItems();
  }

  _onDragStop(event) {
    const draggedEl = event.data.source;
    const snapshot = this._checkedStateSnapshot;
    this._checkedStateSnapshot = null;

    this.element.classList.remove('is-dragging');

    const sourceCard = draggedEl.querySelector(this.cardSelectorValue);
    if (sourceCard) sourceCard.classList.remove('hidden');

    const draggedId = draggedEl.dataset.id;

    if (this.over && (this.before || this.after)) {
      const remaining = this._prospectiveOrder();

      if (this.before) {
        this.over.parentNode.insertBefore(draggedEl, this.over);
      } else {
        this.over.parentNode.insertBefore(draggedEl, this.over.nextElementSibling);
      }

      this._submitReorder(draggedEl, remaining);

      requestAnimationFrame(() => {
        const liveItem = this.itemTargets.find(t => t.dataset.id === draggedId);
        if (!liveItem) return;

        const liveCard = liveItem.querySelector(this.cardSelectorValue);
        if (!liveCard) return;

        const accordion = this.componentsAccordionOutlets
          .find(c => c.element === liveCard);

        if (accordion) accordion.expand();

        this._renumberItems();

        // Double-rAF: lets Draggable's mirror cleanup settle before writing
        // checked state
        requestAnimationFrame(() => {
          if (snapshot) {
            liveCard
              .querySelectorAll('input[type="radio"], input[type="checkbox"]')
              .forEach(input => {
                const wasChecked = snapshot.get(input.name);
                if (wasChecked === undefined) {
                  return;
                }

                input.defaultChecked = wasChecked;
                input.checked = wasChecked;
              });
          }
        });

        document.dispatchEvent(new CustomEvent('promote:highlight', {
          detail: { element: liveCard, scroll: false }
        }));

        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('promote:highlight-removed', {
            detail: { element: liveCard }
          }));
        }, 3000);
      });
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!snapshot) return;

        draggedEl
          .querySelectorAll('input[type="radio"], input[type="checkbox"]')
          .forEach(input => {
          const wasChecked = snapshot.get(input.name);
          if (wasChecked === undefined) {
            return;
          }

          input.defaultChecked = wasChecked;
          input.checked = wasChecked;
        });
      }));
    }

    const placeholder = draggedEl.querySelector('.placeholder');
    if (placeholder) {
      placeholder.classList.add('hidden');
      placeholder.style.height = '';
    }

    this.mirror = null;
    this._draggedId = null;

    this._removeAllDividers();
    this.over = null;
    this.before = null;
    this.after = null;
  }

  // The order the list would end up in if the card were dropped right now, or the
  // current order when there is nothing to drop onto.
  _prospectiveOrder() {
    const ids = this._liveItems().map(item => item.dataset.id);
    const draggedId = this._draggedId;
    const overId = this.over && this.over.dataset.id;

    if (!draggedId || !overId || !(this.before || this.after)) return ids;

    const remaining = ids.filter(id => id !== draggedId);
    const overIdx = remaining.indexOf(overId);

    if (overIdx === -1) return ids;

    remaining.splice(this.before ? overIdx : overIdx + 1, 0, draggedId);

    return remaining;
  }

  _renumberItems(order = null) {
    if (!this.hasQuestionNumberFormatValue) return;

    const items = this._liveItems();
    const sequence = order || items.map(item => item.dataset.id);

    sequence.forEach((id, index) => {
      const item = items.find(candidate => candidate.dataset.id === id);

      if (item) this._writeNumber(item, index + 1);

      // The source card is hidden during the drag, so the number the user actually
      // reads is the one on the floating mirror.
      if (this.mirror && id === this._draggedId) {
        this._writeNumber(this.mirror, index + 1);
      }
    });
  }

  _writeNumber(container, number) {
    const label = container.querySelector('[data-position]');

    if (label) {
      label.textContent = this.questionNumberFormatValue.replace('{n}', number);
    }
  }

  // Draggable clones the dragged card twice: `source` is the clone it moves around,
  // the original stays behind hidden, and the mirror is a third clone. All three keep
  // the item target attribute and the same data-id, so counting them all makes the
  // list look longer than it is and the numbers skip.
  _liveItems() {
    const seen = new Set();

    return this.itemTargets.filter(item => {
      if (item.classList.contains('draggable-mirror')) return false;
      if (item.classList.contains('draggable--original')) return false;

      const id = item.dataset.id;

      // The classes above are only applied after drag:start has been triggered, so
      // the id keeps the clone and the original from both being counted there too.
      if (seen.has(id)) return false;

      seen.add(id);

      return true;
    });
  }

  _submitReorder(draggedEl, newIds) {
    const activeEl = this.element.querySelector('.active[data-id]');
    const selectedPageId = activeEl ? activeEl.dataset.id : draggedEl.dataset.id;

    const body = new FormData();
    newIds.forEach(id => body.append('ids[]', id));
    body.append('selected_page_id', selectedPageId);

    Rails.ajax({
      url: this.urlValue,
      type: 'PATCH',
      data: body,
      dataType: 'script',
      error: (_response) => {
        if (this._originalOrderIds) {
          const items = this._liveItems();

          this._originalOrderIds.forEach(id => {
            const item = items.find(candidate => candidate.dataset.id === id);

            if (item) this.element.appendChild(item);
          });

          this._originalOrderIds = null;
          this._renumberItems();
        }
        document.dispatchEvent(new CustomEvent('promote:display-error-message', {
          bubbles: true,
          detail: { message: this.reorderErrorMessageValue }
        }));
      }
    });
  }

  _removeAllDividers() {
    this.element.querySelectorAll('.divider-above, .divider-below').forEach(el => {
      el.classList.remove('divider-above', 'divider-below');
    });
  }

  _calculateOverlapPercentage(event) {
    const { clientY } = event;
    const overRect = this.over.getBoundingClientRect();
    const midPointY = overRect.top + overRect.height / 2;

    return { hasCrossedHalfwayY: clientY > midPointY };
  }
}
